import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux';

import _ from 'lodash';
import cx from "classnames";
import { Row, Col } from 'react-bootstrap';

import { Loader } from 'components/ApplicationLoader';
import shortid from 'shortid';
import { AssetCard, GroupCardRender, AssetCardRender, StreamCard, RenderEmptyMessage } from './components';
import { fetchGroupRequest, fetchHubStreamsRequest, fetchStreamRequest, invalidateGroup, invalidateStream, uploadStreamAsset } from 'actions/paperflite';
import { AssetUploaderFooter } from '../allContents/components';
import { deletePaperfliteAsset } from 'actions/content';
import InfiniteScroll from 'components/InfiniteScroll';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';

import styles from './paperfliteAsset.module.scss';

export const PaperfliteAssetManager = ({ handleModalClose, handleSave, container }) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    selectedStream: null,
    groupPath: [],
    currentGroup: null
  })

  useEffect(() => {
    dispatch(fetchHubStreamsRequest())

    return () => {
    }
  }, [])

  const uploadedAssets = useSelector((state) =>
    (state.content.uploads || []).filter(
      (asset) => _.get(asset, 'asset.source') === 'paperflite'
    )
  );

  const handleOnClick = (data) => {
    const entityType = _.get(data, 'entityType', null);

    switch (entityType) {
      case 'stream':
        const streamRequest = {
          id: _.get(data, 'id'),
          limit:20,
          sort: 'DESC',
          type: 'name'
        }

        setState((state) => {
          return {
            ...state,
            selectedStream: data
          }
        })
        dispatch(fetchStreamRequest(streamRequest, 0))
        return;

      case 'group':
        setState((state) => {
          return {
            ...state,
            groupPath: [...state.groupPath, data],
            currentGroup: data
          }
        })
        dispatch(fetchGroupRequest(data.id))
        return;

      case 'asset':
        const existingAssset = _.some(uploadedAssets, (assetContent) => _.get(assetContent, 'asset.id') === data.id); // Returns true

        if (!existingAssset) {
          const assetRequest = {
            id: shortid(),
            entityType: 'asset',
            source: 'PAPERFLITE',
            asset: {
              ...data,
              source: 'paperflite'
            }
          }
          dispatch(uploadStreamAsset(assetRequest));
        }
        return;

      default:
        return;
    }
  }

  const navigateBack = (data) => {
    const entityType = _.get(data, 'entityType', null);

    switch (entityType) {
      case 'stream':
        setState((state) => {
          return {
            ...state,
            groupPath: [],
            currentGroup: null
          }
        })

        dispatch(invalidateGroup())
        return;

      case 'group':
        const newPath = (state.groupPath).slice(0, data.index + 1)

        setState((state) => {
          return {
            ...state,
            groupPath: newPath,
            currentGroup: data
          }
        })

        return;

      default:
        setState((state) => {
          return {
            ...state,
            selectedStream: null,
            groupPath: [],
            currentGroup: null
          }
        })
        dispatch(invalidateStream())
        return;
    }
  }

  const handleDelete = (id) => {
    if (id) {
      dispatch(deletePaperfliteAsset(id));
    }
  }


  return (
    <div className={styles.wrapperConatiner}>
      <Row className={styles.layoutRow}>
        <Col className={styles.assetsLayout} xs={8}>
          <HeaderBreadCrumb
            stream={state.selectedStream}
            groupPath={state.groupPath}
            navigateBack={navigateBack}
            container={container}
          />
          {!state.selectedStream &&
            <StreamsListRenderView
              handleOnClick={handleOnClick}
            />
          }
          {state.selectedStream &&
            <>
              {!state.currentGroup &&
                <StreamGroupRenderView
                  mappings={state.selectedStream}
                  type='stream'
                  handleOnClick={handleOnClick}
                />
              }
              {state.currentGroup &&
                <StreamGroupRenderView
                  mappings={state.currentGroup}
                  type='group'
                  handleOnClick={handleOnClick}
                />
              }
            </>
          }
        </Col>
        <Col className={styles.selectionList} xs={4}>
          <div className={styles.header}>
            <span className={styles.assetType}>Selected:</span>
            <span className={styles.folderPath}>{_.size(uploadedAssets)} Assets</span>
          </div>
          <div className={styles.selectedAssetView}>
            {_.map(uploadedAssets, (asset) => {
              return (
                <AssetCard
                  key={_.get(asset, 'id', null)}
                  className={styles.assetCard}
                  id={_.get(asset, 'id', null)}
                  assetMapping={asset}
                  handleDelete={handleDelete}
                  onClick={() => { console.log('') }}
                />
              )
            })
            }
          </div>
          <AssetUploaderFooter
            className={styles.footer}
            handleSave={handleSave}
            handleClose={handleModalClose}
          />
        </Col>
      </Row>
    </div>
  )
}


const StreamsListRenderView = ({ handleOnClick }) => {

  const streams = useSelector(state => _.get(state, 'paperflite.items', [])) || [];
  const loading = useSelector(state => _.get(state, 'paperflite.loading', false));

  return (
    <div className={cx(styles.streamsMapContainer, { [styles.loadingContainer]: (_.size(streams) == 0 || loading) })}>
      {loading &&
        <div className={styles.loadingAnimationContainer}>
          <Loader className={styles.loadingAnimation} title="Loading..." />
        </div>
      }
      {(!loading && _.size(streams) == 0) &&
        renderEmptyMessage('stream')
      }
      {(_.size(streams) > 0 && !loading) &&
        _.map(streams, (stream, index) => {
          return (
            <StreamCard
              key={index}
              onSelect={handleOnClick}
              stream={stream}
            />
          )
        })
      }
    </div>
  )
}

const StreamGroupRenderView = ({ mappings, type, handleOnClick }) => {
  const dispatch = useDispatch();
  const id = _.get(mappings, 'id');
  const childrens = useSelector(state => _.get(state, `paperflite.${type}.${id}.item.children`, [])) || [];
  const loading = useSelector(state => _.get(state, `paperflite.${type}.${id}.loading`, false));
  const currentPage = useSelector(state => _.get(state, `paperflite.stream.${id}.item.currentPage`, {}));
  const totalPages = useSelector(state => _.get(state, `paperflite.stream.${id}.item.totalPages`, {}));
  const hasMoreItems = currentPage < totalPages - 1;

  const loadMoreAssets = () => {
    const nextPage = currentPage + 1;
    if (nextPage >= totalPages) {
      return;
    }
    handleFetchAssets(nextPage);
  }

  const handleFetchAssets = (page) => {
    var streamRequest = {
      id: id,
      limit: 20,
      sort: 'DESC',
      type: 'name'
    }

    dispatch(fetchStreamRequest(streamRequest, page));
  }

  return (
    <div className={cx(styles.streamGroupMapContainer, { [styles.loadingContainer]: (_.size(childrens) == 0 || loading) })}>
      {loading &&
        <div className={styles.loadingAnimationContainer}>
          <Loader className={styles.loadingAnimation} title="Loading..." />
        </div>
      }
      {(!loading && _.size(childrens) == 0) &&
        renderEmptyMessage('group')
      }
      {(_.size(childrens) > 0 && !loading) &&
      <InfiniteScroll
        className={styles.scrollable}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={() => {
          loadMoreAssets();
        }}
      >
        {
          _.map(childrens, (children, index) => {
            if (_.get(children, 'entityType') === 'asset') {
              return (
                <AssetCardRender
                  key={index}
                  asset={children}
                  onSelect={handleOnClick}
                />
              )
            }
  
            return (
              <GroupCardRender
                key={index}
                onSelect={handleOnClick}
                group={children}
              />
            )
          })
        }
      </InfiniteScroll>
      }
    </div>
  )
}

const HeaderBreadCrumb = ({ stream, groupPath, navigateBack, container }) => {
  return (
    <div className={styles.header}>
      <span className={styles.assetType}>Paperflite:</span>
      <span onClick={() => { navigateBack() }} className={cx(styles.streamName, { [styles.activePath]: (!stream && _.size(groupPath) == 0) })}>Streams</span>
      {stream &&
        <span className={cx(styles.streamPath, { [styles.activePath]: (stream && _.size(groupPath) == 0) })}>
          <i className={cx(styles.backIcon, 'cleverstory-drop-down-right')} />
          <TruncatedTextWithTooltip 
            content={_.get(stream, 'name')} 
            maxWidth={200} 
            placement='bottom'
            onClick={() => {navigateBack(stream)}}
            container={container}
          />
        </span>
      }
      {(_.size(groupPath) > 0) &&
        _.map(groupPath, (path, index) => {
          const groupData = {
            ...path,
            index
          }
          return (
            <span key={index} className={cx(styles.folderPath, { [styles.activePath]: _.size(groupPath) == (index + 1) })}>
              <i className={cx(styles.backIcon, 'cleverstory-drop-down-right')} />
              <TruncatedTextWithTooltip 
                content={path.name} 
                maxWidth={200} 
                placement='bottom'
                onClick={() => {navigateBack(groupData)}}
                container={container}
              />
            </span>
          )
        })
      }
    </div>
  )
}

const renderEmptyMessage = (content) => {
  return (
    <div className={styles.emptyMessageContainer}>
      <div className={styles.emptyMessage}>
        <span>Ginger. Anybody in?<br /></span>
        <span>{`There is no content in this ${content} yet.`}</span>
      </div>
    </div>
  )
}
